import { Button, Card, Dropdown, Skeleton, Space, Table, Tag, Typography } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp, faEdit, faEllipsisV, faGripDotsVertical } from "@fortawesome/pro-solid-svg-icons"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from '@dnd-kit/utilities'
import { HtmlHTMLAttributes, useState } from "react"
import { DndContext, DragEndEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { AUTOMATIC_TAX_ROW_ID, buildTaxRateTableRows, EnrichedTaxRate, enrichTaxRates } from "./invoicingSettingsModel"
import CreateTaxRateModal from "./CreateTaxRateModal"
import { formatRatePercent, TaxRate } from "../../../domain/invoicing/taxRate"
import EditTaxRateModal from "./EditTaxRateModal"
import { useChangeAutomaticTaxRateActiveStatusMutation, useGetTaxRatesQuery, useMakeAutomaticTaxRateDefaultMutation, useReorderTaxRatesMutation, useSetDefaultTaxRateMutation, useUpdateTaxRateActiveStatusMutation } from "../../../domain/invoicing/taxRatesQueries"
import { InvoicingSettings } from "../../../domain/invoicing/settings/invoicingSettings"
import useFeatureFlag from "../../../hooks/useFeatureFlag"
import LabeledSwitch from "../../common/LabeledSwitch"

type Props = {
  serverSettings: InvoicingSettings
}

const TaxRatesCard = ({ serverSettings }: Props) => {
  const isAutomaticTaxEnabled = useFeatureFlag('automatic-tax-rates')
  const taxRatesQuery = useGetTaxRatesQuery()
  const taxRates = enrichTaxRates(taxRatesQuery.data ?? [])
  const rows = buildTaxRateTableRows(taxRatesQuery.data ?? [], isAutomaticTaxEnabled, serverSettings.automaticTaxIsDefault, serverSettings.automaticTaxIsActive)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingTaxRate, setEditingTaxRate] = useState<TaxRate | undefined>(undefined)
  const setDefaultTaxRateMutation = useSetDefaultTaxRateMutation()
  const makeAutomaticTaxRateDefaultMutation = useMakeAutomaticTaxRateDefaultMutation()
  const changeAutomaticTaxRateActiveStatusMutation = useChangeAutomaticTaxRateActiveStatusMutation()
  const reorderTaxRatesMutation = useReorderTaxRatesMutation()
  const updateTaxRateActiveStatusMutation = useUpdateTaxRateActiveStatusMutation()

  if (serverSettings.invoicingSystem !== 'synkedUp') {
    return <></>
  }

  if (taxRatesQuery.isLoading) {
    return <Skeleton active />
  }

  const onDragEnd = ({active, over}: DragEndEvent) => {
    if (active && over) {
      const oldIndex = taxRates.findIndex(x => x.id === active.id)
      const newIndex = taxRates.findIndex(x => x.id === over.id)
      reorderTaxRates(oldIndex, newIndex)
    }
  }

  const handleMoveUpOrDown = (taxRate: EnrichedTaxRate, direction: 'up' | 'down') => {
    if (direction === 'up' && !taxRate.canMoveUp) {
      return
    }
    
    if (direction === 'down' && !taxRate.canMoveDown) {
      return
    }

    const oldIndex = taxRates.findIndex(x => x.id === taxRate.id)
    const newIndex = direction === 'up' ? oldIndex - 1 : oldIndex + 1
    reorderTaxRates(oldIndex, newIndex)
  }

  const reorderTaxRates = (oldIndex: number, newIndex: number) => {
    const newOrder = arrayMove(taxRates, oldIndex, newIndex)
      .map((x, sortIndex) => ({taxRateId: x.id, oldSortIndex: x.sortIndex, sortIndex: sortIndex}))
      .filter(x => x.oldSortIndex !== x.sortIndex)
    reorderTaxRatesMutation.mutate(newOrder)
  }

  return <Card
    title={<div style={{ margin: "16px 0" }}>
      Tax Rates
      <br/>
      <Typography.Text type="secondary" style={{fontWeight: 'normal', textWrap: 'wrap'}}>
        Add and manage the tax rates for your organization.
        You can choose which rate to use when creating estimates and invoices.
      </Typography.Text>
    </div>}
    extra={<Space>
      <Button type="primary" onClick={() => setIsCreateModalOpen(true)}>+ Add Tax Rate</Button>
    </Space>}
  >
    <DndContext onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={taxRates.map(x => x.id)} strategy={verticalListSortingStrategy}>
        <Table
          components={{
            header: { row: SortableHeader },
            body: { row: TableRow },
          }}
          rowKey="id"
          dataSource={rows}
          columns={[
            {
              title: 'Name',
              dataIndex: 'name',
              render: (_, x) => {
                if (x.kind === 'automatic') {
                  return <span>
                    Calculate Automatically
                    <br/>
                    <Typography.Text type="secondary">Automatically calculated when creating an invoice</Typography.Text>
                  </span>
                }
                return x.name
              },
            },
            {
              title: 'Rate',
              dataIndex: 'rate',
              render: (_, x) => {
                if (x.kind === 'automatic') {
                  return <></>
                }
                return formatRatePercent(x.rate)
              },
            },
            {
              title: 'Default Status',
              dataIndex: 'isDefault',
              render: (_, x) => {
                if (x.isDefault) {
                  return <Tag>Default</Tag>
                }
                if (x.kind === 'automatic') {
                  if (!x.isActive) {
                    return <></>
                  }
                  return <Typography.Link onClick={() => makeAutomaticTaxRateDefaultMutation.mutate()}>Set as Default</Typography.Link>
                }
                return <Typography.Link onClick={() => setDefaultTaxRateMutation.mutate(x.id)}>Set as Default</Typography.Link>
              }
            },
            {
              title: 'Last Updated',
              dataIndex: 'lastSignificantUpdateAt',
              render: (_, x) => {
                if (x.kind === 'automatic') {
                  return <></>
                }
                return <span>
                  {new Date(x.lastSignificantUpdateAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  <br/>
                  <Typography.Text type="secondary">{new Date(x.lastSignificantUpdateAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</Typography.Text>
                </span>
              }
            },
            {
              title: 'Active Status',
              dataIndex: 'isActive',
              render: (_, x) => {
                if (x.kind === 'automatic') {
                  return <LabeledSwitch
                    label={x.isActive ? 'Active' : 'Inactive'}
                    value={x.isActive}
                    loading={changeAutomaticTaxRateActiveStatusMutation.isLoading}
                    disabled={changeAutomaticTaxRateActiveStatusMutation.isLoading}
                    onChange={() => changeAutomaticTaxRateActiveStatusMutation.mutate(!x.isActive)}
                  />
                }
                return <LabeledSwitch
                  label={x.isActive ? 'Active' : 'Inactive'}
                  value={x.isActive}
                  onChange={() => updateTaxRateActiveStatusMutation.mutate({ taxRateId: x.id, isActive: !x.isActive })}
                />
              }
            },
            {
              title: '',
              render: (_, x) => {
                if (x.kind === 'automatic') {
                  return <></>
                }
                return <TaxRateActions
                  taxRate={x}
                  onEdit={() => setEditingTaxRate(x)}
                  onMoveUp={() => handleMoveUpOrDown(x, 'up')}
                  onMoveDown={() => handleMoveUpOrDown(x, 'down')}
                />
              },
            }
          ]}
          pagination={{
            pageSize: 50,
            hideOnSinglePage: true,
          }}
        />
      </SortableContext>
    </DndContext>
    <CreateTaxRateModal
      isOpen={isCreateModalOpen}
      onClose={() => setIsCreateModalOpen(false)}
      nextSortIndex={Math.max(0, ...taxRates.map(x => x.sortIndex)) + 1}
    />
    <EditTaxRateModal
      isOpen={editingTaxRate !== undefined}
      onClose={() => setEditingTaxRate(undefined)}
      taxRate={editingTaxRate}
    />
  </Card>
}

export default TaxRatesCard

type TaxRateActionsProps = {
  taxRate: EnrichedTaxRate
  onEdit: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

const TaxRateActions = ({taxRate, onEdit, onMoveUp, onMoveDown}: TaxRateActionsProps) => {
  return <Dropdown
    trigger={['click']}
    menu={{
      items: [
        {
          key: 'edit',
          label: 'Edit',
          icon: <FontAwesomeIcon icon={faEdit} />,
          onClick: (e) => {
            e.domEvent.stopPropagation()
            onEdit()
          }
        },
        {
          key: 'move-up',
          label: 'Move Up',
          icon: <FontAwesomeIcon icon={faArrowUp} />,
          onClick: (e) => {
            e.domEvent.stopPropagation()
            onMoveUp()
          },
          disabled: !taxRate.canMoveUp
        },
        {
          key: 'move-down',
          label: 'Move Down',
          icon: <FontAwesomeIcon icon={faArrowDown} />,
          onClick: (e) => {
            e.domEvent.stopPropagation()
            onMoveDown()
          },
          disabled: !taxRate.canMoveDown
        }
      ]
    }}
  >
    <Button 
      type="text" 
      icon={<FontAwesomeIcon icon={faEllipsisV} />} 
      onClick={e => e.stopPropagation()}
    />
</Dropdown>
}

type SortableRowProps = HtmlHTMLAttributes<HTMLTableRowElement> & {
  'data-row-key': string
}

const TableRow = (props: SortableRowProps) => {
  if (props['data-row-key'] === AUTOMATIC_TAX_ROW_ID) {
    return <StaticRow {...props} />
  }
  return <SortableRow {...props} />
}

const StaticRow = (props: SortableRowProps) => {
  const filteredProps = Object.fromEntries(Object.entries(props).filter(([k]) => k !== 'getData'))

  return <tr {...filteredProps}>
    <td></td>
    {props.children}
  </tr>
}

const SortableRow = (props: SortableRowProps) => {
  const id = props['data-row-key']

  const { setNodeRef, setActivatorNodeRef, transform, transition, attributes, listeners } = useSortable({
    id,
    data: { templateItemId: id }
  })

  const style: React.CSSProperties = {
    ...props.style,
    transition,
    transform: CSS.Transform.toString(transform),
  }

  const filteredProps = Object.fromEntries(Object.entries(props).filter(([k]) => k !== 'getData'))

  return <tr {...filteredProps} ref={setNodeRef} style={style}>
    <td>
      <span ref={setActivatorNodeRef} style={{ fontSize: 18, cursor: 'move' }} {...attributes} {...listeners}><FontAwesomeIcon icon={faGripDotsVertical} /></span>
    </td>
    {props.children}
  </tr>
}

const SortableHeader = (props: SortableRowProps) => {
  return <tr {...props}>
    <th></th>
    {props.children}
  </tr>
}